import { Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { PaginatorState } from 'primeng/paginator';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { PrestadorHistoricoCorrespondencia } from '@/core/api/sicom-api/registro/prestador/prestador-historico.model';
import { PrestadorHistoricoService } from '@/core/api/sicom-api/registro/prestador/prestador-historico.service';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { DateUtils } from '@/shared/utils/date-utils';

@Component({
    selector: 'app-correspondencias-prestador',
    standalone: true,
    imports: [CardContent, CustomTableComponent],
    templateUrl: './correspondencias-prestador.html'
})
export class CorrespondenciasPrestador {
    pessoa = input<PessoaDomain>();
    private readonly destroyRef = inject(DestroyRef);
    private readonly service = inject(PrestadorHistoricoService);
    readonly correspondencias = signal<PrestadorHistoricoCorrespondencia[]>([]);
    readonly first = signal(0);
    readonly rows = signal(10);
    readonly totalRecords = signal(0);
    readonly loading = signal(false);
    readonly correspondenciasTabela = computed(() => this.correspondencias().map((item) => ({ ...item, dataCorrespondencia: DateUtils.formatDate(item.dataCorrespondencia) })));
    readonly metadata: TableMetadata = {
        headers: {
            numero: { type: 'text', value: 'Número', default: '-', hidden: false },
            tipoCorrespondencia: { type: 'text', value: 'Tipo Correspondência', default: '-', hidden: false },
            dataCorrespondencia: { type: 'text', value: 'Data Correspondência', default: '-', hidden: false },
            setor: { type: 'text', value: 'Setor', default: '-', hidden: false }
        },
        title: 'numero',
        defaultSortField: 'dataCorrespondencia',
        defaultSortOrder: -1
    };

    constructor() {
        effect(() => {
            const pessoa = this.pessoa();
            if (pessoa?.id && pessoa.uf) {
                untracked(() => this.carregar());
            } else {
                this.correspondencias.set([]);
            }
        });
    }

    private carregar(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) return;

        this.loading.set(true);
        this.service
            .listarCorrespondencias(pessoa.id, pessoa.uf, Math.floor(this.first() / this.rows()), this.rows())
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.loading.set(false))
            )
            .subscribe({
                next: (response) => {
                    this.correspondencias.set(response.content ?? []);
                    this.totalRecords.set(response.totalElements ?? 0);
                },
                // Sem isto, a falha na troca de prestador deixaria os dados do anterior na tela.
                error: () => {
                    this.correspondencias.set([]);
                    this.totalRecords.set(0);
                }
            });
    }

    onPaginaAlterada(event: PaginatorState): void {
        this.first.set(event.first ?? 0);
        this.rows.set(event.rows ?? 10);
        this.carregar();
    }
}
